import { AdService, IAdServiceConfig } from '..'
import { IDOMConf } from '../../../domain/conf'
import { Num } from '../../../utils/common'
import { DOMEvents } from '../../../utils/dom'
import { Mixin } from '../../../utils/mixin'
import { ScreenUtils } from '../../../utils/screen'
import { AdEvents, IAdEvent } from '../ad/events'
import { IPageTargetingConfig } from '../targeting/default'

export interface IAdRefreshConf {
    /**
     * Maximum number of times ads will be refreshed.
     */
    max?: number

    /**
     * Seconds between refreshes.
     */
    s?: number
}

export interface IAdRefreshDOMConf extends IDOMConf {
    attr: {
        /**
         * Enables/disables refresh.
         */
        state: {
            key: 'data-ad-ref'
            value?: {
                off: 'off'
            }
        }
    }
}

export const AdRefreshDOM: IAdRefreshDOMConf = {
    attr: {
        state: {
            key: 'data-ad-ref',
            value: {
                off: 'off'
            }
        }
    }
}

export const WithRefresh = <T extends Mixin<AdService>>(Base: T) =>

    class AdRefresh extends Base {
        public static defaultConf: IAdRefreshConf = {
            max: 5,
            s: 30
        }
        public static threshold = 0.3 // portion of ad that must be visible
        protected refreshActive: { [id: string]: boolean }
        protected refreshConf: IAdRefreshConf
        protected refreshCount = 0
        protected refreshElapsedSec = 0
        protected refreshLastMs: number
        protected refreshListenerAdLoaded: (e: Event) => void
        protected refreshListenerIdle: () => void
        protected refreshListenerVisible: () => void
        protected refreshObserver: IntersectionObserver
        constructor(...args: any[]) {
            super(...args)

            // bind and store listeners (for removal)
            this.refreshListenerAdLoaded = this.refreshOnAdLoaded.bind(this)
            this.refreshListenerIdle = this.refreshOnIdle.bind(this)
            this.refreshListenerVisible = this.refreshOnVisible.bind(this)
        }

        public async initialize(conf: IAdServiceConfig) {
            if (conf.refresh === true) {
                this.refreshConf = AdRefresh.defaultConf
            // disable default behaviour so can release before WP changes (enable after):
            // } else if (conf.refresh !== false) {
            } else if (conf.refresh !== false && conf.refresh !== undefined) {
                this.refreshConf = { ...AdRefresh.defaultConf, ...conf.refresh }
            }
            return super.initialize(conf)
        }

        /**
         * Enables or disables ad refreshing according to 'ad light' state.
         */
        public async update(adLight: boolean) {
            await super.update(adLight)

            if (this.refreshConf) {
                if (adLight) {
                    this.refreshDisable()
                } else if (this.refreshCount < this.refreshConf.max) {
                    this.refreshEnable()
                }
            }
        }

        protected shouldRefresh(el: Element) {
            return this.refreshConf
                && el.getAttribute(AdRefreshDOM.attr.state.key) !== AdRefreshDOM.attr.state.value.off
        }

        /**
         * Destroys and refreshes elligible registered ads.
         */
        protected refresh() {
            const ads = this.adapter.ads.filter((_) => {
                return this.shouldRefresh(_.el)
            })
            this.refreshActive = {}
            this.refreshElapsedSec = 0
            delete this.refreshLastMs

            // remove ads (out-of-page before adapter)
            AdService.destroyOOP(ads)
            this.adapter.destroy(ads)

            // set count as page target
            this.refreshCount += 1
            this.adapter.target(this.targeting.page({ refreshCount: this.refreshCount } as IPageTargetingConfig))

            // last refresh, we're done
            if (this.refreshCount >= this.refreshConf.max) {
                this.refreshDisable()
            }

            // refresh ads
            this.registered = false
            this.registerAds(ads).catch(console.error)
        }

        /**
         * Removes listeners etc.
         */
        protected refreshDisable() {
            document.removeEventListener(AdEvents.DISPLAY, this.refreshListenerAdLoaded)
            document.removeEventListener(DOMEvents.VISIBILITY, this.refreshListenerVisible)
            if (this.refreshObserver) {
                this.refreshObserver.disconnect()
            }
            delete this.refreshActive
            delete this.refreshObserver
        }

        /**
         * Adds listeners etc.
         */
        protected refreshEnable() {
            this.refreshObserver = new IntersectionObserver(this.refreshObserve.bind(this), {
                threshold: AdRefresh.threshold
            })
            document.addEventListener(AdEvents.DISPLAY, this.refreshListenerAdLoaded)
            document.addEventListener(DOMEvents.VISIBILITY, this.refreshListenerVisible)
        }

        /**
         * `IntersectionObserver` callback. Enables/disables timing according to visibility.
         */
        protected refreshObserve(entries: IntersectionObserverEntry[]) {
            entries.forEach((entry) => this.refreshTimerEnable(entry.target.id, entry.isIntersecting))
        }

        /**
         * Ad display event listener. Registers ad elements with `IntersectionObserver`.
         */
        protected refreshOnAdLoaded(e: IAdEvent) {
            const ad = e.detail

            if (!this.shouldRefresh(ad.el)) {
                return
            }

            this.refreshObserver.observe(ad.el)

            // observer only notifies of changes, so enable timer if already visible
            this.refreshTimerEnable(ad.id, ScreenUtils.inView(ad.el, AdRefresh.threshold))
        }

        /**
         * Idle callback. Handles timing, initiates refresh when time elapsed.
         */
        protected refreshOnIdle() {
            const now = Date.now()

            // accrue time
            if (this.refreshLastMs) {
                this.refreshElapsedSec += (now - this.refreshLastMs) / Num.secToMs
            }

            // time elapsed, refresh
            if (this.refreshElapsedSec >= this.refreshConf.s) {
                return this.refresh()
            }

            // document and ad(s) visible, continue to watch time
            this.refreshLastMs = now
            if (!document.hidden && this.refreshActive && Object.values(this.refreshActive).filter((_) => _).length) {
                window.requestAnimationFrame(this.refreshListenerIdle)
            }
        }

        /**
         * Visibility state change listener. Allows timing to stop when document not being viewed.
         */
        protected refreshOnVisible() {
            if (document.visibilityState === 'visible') {
                window.requestAnimationFrame(this.refreshListenerIdle)
            } else {
                delete this.refreshLastMs
            }
        }

        /**
         * Enables/disables timing on a given ad. Called as ads enter/leave viewport.
         */
        protected refreshTimerEnable(id: string, on: boolean) {
            this.refreshActive = this.refreshActive || {}
            const alreadyActive = Object.values(this.refreshActive).filter((_) => _).length
            this.refreshActive[id] = on
            if (on && !alreadyActive) {
                window.requestAnimationFrame(this.refreshListenerIdle)
            }
        }
    }
